import { useEffect, useLayoutEffect, useRef, useState, type SyntheticEvent } from 'react';
import { createPortal } from 'react-dom';
import { guidHead, isGuid } from '@/shared/lib';

/** Space between the value and its tooltip, and between the tooltip and the window edge. */
const GAP = 6;
const EDGE = 8;
/** Lets the pointer cross the gap into the tooltip (to select the value) before it closes. */
const HIDE_DELAY = 150;

/** Clicks in the tooltip select its text; they must not open the row or follow a parent link. */
const stop = (event: SyntheticEvent) => {
  event.stopPropagation();
};

/**
 * `span.guid`: a GUID shown as its first group (`f2f99afc…`) to save space in grids.
 * The rest stays in the DOM (visually hidden), so link names, copy and find see the full value;
 * hovering shows the full value in a tooltip, where one click selects it for copying.
 */
export function Guid({ value }: { value: string }) {
  const anchor = useRef<HTMLSpanElement>(null);
  const tip = useRef<HTMLSpanElement>(null);
  const hideTimer = useRef<ReturnType<typeof setTimeout>>(undefined);
  const [open, setOpen] = useState(false);

  const show = () => {
    clearTimeout(hideTimer.current);
    setOpen(true);
  };
  const hide = () => {
    clearTimeout(hideTimer.current);
    hideTimer.current = setTimeout(() => {
      setOpen(false);
    }, HIDE_DELAY);
  };

  // Below the value, or above it near the bottom of the window; never past the right edge.
  useLayoutEffect(() => {
    if (!open || !anchor.current || !tip.current) return;
    const a = anchor.current.getBoundingClientRect();
    const t = tip.current.getBoundingClientRect();
    const below = a.bottom + GAP;
    const top = below + t.height > window.innerHeight - EDGE ? a.top - GAP - t.height : below;
    const left = Math.max(EDGE, Math.min(a.left, window.innerWidth - EDGE - t.width));
    tip.current.style.top = `${String(top)}px`;
    tip.current.style.left = `${String(left)}px`;
  }, [open]);

  // The tooltip is fixed to the window, so it closes when anything scrolls under it.
  useEffect(() => {
    if (!open) return;
    const close = () => {
      setOpen(false);
    };
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    return () => {
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
    };
  }, [open]);

  useEffect(
    () => () => {
      clearTimeout(hideTimer.current);
    },
    [],
  );

  if (!isGuid(value)) return <>{value}</>;
  const head = guidHead(value);
  return (
    <>
      <span ref={anchor} className="guid" onMouseEnter={show} onMouseLeave={hide}>
        {head}
        <span className="visually-hidden">{value.slice(head.length)}</span>
      </span>
      {open &&
        createPortal(
          <span
            ref={tip}
            className="guid-tip"
            role="tooltip"
            onMouseEnter={show}
            onMouseLeave={hide}
            onMouseDown={stop}
            onClick={stop}
            onDoubleClick={stop}
          >
            {value}
          </span>,
          document.body,
        )}
    </>
  );
}
